// web/src/lib/scryfall.ts
import { scry, scryImageUrl } from "@/lib/scryClient";
import type { ScryfallCard } from "@/types/cards";

export type ScryfallRuling = {
  object: "ruling";
  source: string;        // "wotc" | "scryfall"
  published_at: string;  // ISO date
  comment: string;
};

/* ----------------------- small utils ----------------------- */

function sortByReleaseAsc(cards: ScryfallCard[]): ScryfallCard[] {
  return [...cards].sort(
    (a, b) => (a.released_at ?? "1900-01-01").localeCompare(b.released_at ?? "1900-01-01")
  );
}

// Normalize list payloads from different backends
function extractList<T = any>(payload: any): T[] {
  if (!payload) return [];
  if (Array.isArray(payload.data)) return payload.data as T[];
  if (Array.isArray(payload.results)) return payload.results as T[];
  if (Array.isArray(payload.cards)) return payload.cards as T[];
  if (Array.isArray(payload)) return payload as T[];
  return [];
}

// Normalize single card payloads (direct or wrapped)
function extractCard(payload: any): ScryfallCard | null {
  if (!payload) return null;
  if (payload.object === "card" && payload.id) return payload as ScryfallCard;
  if (payload.data && payload.data.object === "card") return payload.data as ScryfallCard;
  if (payload.card && payload.card.object === "card") return payload.card as ScryfallCard;
  return null;
}

/* ----------------------------- search -------------------------------- */

export async function searchCards(
  q: string,
  next?: string | null,
  signal?: AbortSignal
): Promise<{ cards: ScryfallCard[]; next: string | null; warnings?: string[] }> {
  // scry.search mirrors Scryfall’s /cards/search but served from your backend
  const payload: any = await scry.search(q, { order: "released", unique: "art", next }, signal);
  return {
    cards: extractList<ScryfallCard>(payload),
    next: (payload?.next ?? payload?.next_page) || null,
    warnings: payload?.warnings,
  };
}

/* ------------------------ single card / prints ------------------------ */

export async function getCardById(id: string, signal?: AbortSignal): Promise<ScryfallCard> {
  const payload: any = await scry.getById(id, signal);
  const card = extractCard(payload);
  if (!card) throw new Error("scry.getById returned no card");
  return card;
}

export async function getCardByIdSafe(id: string, signal?: AbortSignal): Promise<ScryfallCard | null> {
  try {
    const payload: any = await scry.getById(id, signal);
    return extractCard(payload);
  } catch {
    return null;
  }
}

export async function getRulingsByCardId(id: string, signal?: AbortSignal): Promise<ScryfallRuling[]> {
  const payload: any = await scry.rulingsById(id, signal);
  const arr =
    Array.isArray(payload?.data) ? payload.data :
    Array.isArray(payload?.rulings) ? payload.rulings :
    Array.isArray(payload) ? payload : [];
  return arr as ScryfallRuling[];
}

export async function getPrintsByCardId(id: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
  const payload: any = await scry.printsById(id, signal);
  const list = extractList<ScryfallCard>(payload);
  return list;
}

export async function getPrintsByOracleId(oracleId: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
  const payload: any = await scry.printsByOracle(oracleId, signal);
  const list = extractList<ScryfallCard>(payload);
  return list.length ? sortByReleaseAsc(list) : [];
}

/**
 * Get ALL prints for a card, reliably.
 * Prefer oracle_id → id; the backend handles pagination.
 */
export async function getAllPrints(base: ScryfallCard, signal?: AbortSignal): Promise<ScryfallCard[]> {
  if (base?.oracle_id) {
    const viaOracle = await getPrintsByOracleId(base.oracle_id, signal);
    if (viaOracle.length) return sortByReleaseAsc(viaOracle);
  }
  if (base?.id) {
    const viaId = await getPrintsByCardId(base.id, signal);
    if (viaId.length) return sortByReleaseAsc(viaId);
  }
  return [];
}

/* ---------------------------- extra helpers --------------------------- */

export async function searchPrintsByNameExact(name: string, signal?: AbortSignal): Promise<ScryfallCard[]> {
  // exact name + unique=prints to mirror old behavior
  const payload: any = await scry.search(`!"${name}"`, { unique: "prints" }, signal);
  return extractList<ScryfallCard>(payload);
}

export async function namedFuzzy(name: string, signal?: AbortSignal): Promise<ScryfallCard | null> {
  const payload: any = await scry.namedFuzzy(name, signal);
  return extractCard(payload);
}

export async function searchBySetAndNumber(
  name: string,
  setCode: string,
  cn: string,
  signal?: AbortSignal
): Promise<ScryfallCard | null> {
  // keep legacy query semantics
  const q = `!"${name}" set:${setCode} (cn:${cn} OR number:${cn}) unique:prints`;
  const payload: any = await scry.search(q, {}, signal);
  const list = extractList<ScryfallCard>(payload);
  return list.length ? list[0] : null;
}

/* ----------------------------- images -------------------------------- */

export function getImageUrl(
  id: string,
  opts?: { version?: "small" | "normal" | "large" | "png" | "art_crop" | "border_crop"; face?: 0 | 1; format?: "image" | "json" }
): string {
  // passes through to your redirect/proxy helper
  return scryImageUrl(id, opts);
}
